import { inject, watch } from "vue";
import { last, some } from "lodash";

export const useTaskEvents = ({ tasks, filters, isLastPage, callback }) => {
    watch(tasks, (newTasks, oldTasks) => {
        callback(newTasks.length - oldTasks.length);
    });

    const emitter = inject("emitter");

    [
        "task.described",
        "task.added",
        "task.created",
        "task.deleted",
        "task.category-auto-detected",
        "task.assigned-to-category",
        "task.assigned-to-user",
        "task.assignees-cleared",
        "task.category-cleared",
        "task.located",
        "task.priority-auto-detected",
        "task.priority-changed",
        "task.reopened",
        "task.restored",
        "task.scheduled",
        "task.unscheduled",
        "task.completed",
        "task.tagged",
        "task.untagged",
        "task.visibility-toggled",
        "checklist.created",
        "checklist.deleted",
        "checklist-item.created",
        "checklist-item.deleted",
        "checklist-item.checked",
        "checklist-item.unchecked",
    ].forEach((event) =>
        emitter.on(event, ({ task }) =>
            handleTaskEvent({ task, tasks, filters, isLastPage })
        )
    );
};

const handleTaskEvent = ({ task, tasks, filters, isLastPage }) => {
    const isCurrentlyInLastPosition = last(tasks.value)?.id === task.id;
    removeTask(tasks, task);

    if (!taskMatchesFilters(task, filters)) {
        return;
    }

    updateTasks(task, tasks, filters, isLastPage, isCurrentlyInLastPosition);
};

const taskMatchesFilters = (task, filters) => {
    const filterFunctions = [
        taskMatchesTenantFilter,
        taskMatchesCategoryFilter,
        taskMatchesAssigneeFilter,
        taskMatchesSpaceAndAssetFilter,
        taskMatchesPriorityFilter,
        taskMatchesStatusFilter,
        taskMatchesArchivedFilter,
        taskMatchesScheduledForFilter,
        taskMatchesTagsFilter,
        taskMatchesVisibilityFilter,
        taskMatchesTaskTypeFilter,
        taskMatchesSearchQuery,
    ];

    return filterFunctions.every((fn) => fn(task, filters));
};

const taskMatchesTenantFilter = (task, filters) => {
    const tenantFilter = filters.value.tenant;
    return !tenantFilter || tenantFilter.includes(task.tenant.id);
};

const taskMatchesCategoryFilter = (task, filters) => {
    const categoryFilter = filters.value.categories;
    return (
        !categoryFilter ||
        (categoryFilter.includes("no_category") && !task.category) ||
        categoryFilter.includes(task.category?.id)
    );
};

const taskMatchesAssigneeFilter = (task, filters) => {
    const assigneeFilter = filters.value.assignee;
    return (
        !assigneeFilter ||
        (assigneeFilter.includes("unassigned") &&
            task.assignees.length === 0) ||
        some(task.assignees, (assignee) => assigneeFilter.includes(assignee.id))
    );
};

const taskMatchesSpaceAndAssetFilter = (task, filters) => {
    const spaceAndAssetFilter = filters.value.space;
    return !spaceAndAssetFilter || spaceAndAssetFilter.includes(task.space?.id);
};

const taskMatchesPriorityFilter = (task, filters) => {
    const priorityFilter = filters.value.priority;
    return !priorityFilter || priorityFilter.includes(task.priority.id);
};

const taskMatchesStatusFilter = (task, filters) => {
    const statusFilter = filters.value.status;
    return !statusFilter || statusFilter.includes(task.status);
};

const taskMatchesArchivedFilter = (task, filters) => {
    return (
        (filters.value.archived_only && task.deleted_at) ||
        (!filters.value.archived_only && !task.deleted_at)
    );
};

const taskMatchesScheduledForFilter = (task, filters) => {
    const scheduledForFilter = filters.value.scheduled_for;
    return (
        !scheduledForFilter ||
        (scheduledForFilter.gte === null &&
            scheduledForFilter.lte === null &&
            task.scheduled_for === null) ||
        (new Date(task.scheduled_for) >= new Date(scheduledForFilter.gte) &&
            new Date(task.scheduled_for) <= new Date(scheduledForFilter.lte))
    );
};

const taskMatchesTagsFilter = (task, filters) => {
    const tagsFilter = filters.value.tags;
    return (
        !tagsFilter ||
        (tagsFilter.includes("no_tags") && task.tags.length === 0) ||
        some(task.tags, (tag) => tagsFilter.includes(tag.id))
    );
};

const taskMatchesVisibilityFilter = (task, filters) => {
    const visibilityFilter = filters.value.visibility;
    return (
        !visibilityFilter ||
        (visibilityFilter === "private" && task.problem.is_private) ||
        (visibilityFilter === "public" && !task.problem.is_private)
    );
};

const taskMatchesTaskTypeFilter = (task, filters) => {
    const taskTypeFilter = filters.value.task_type;
    return (
        !taskTypeFilter ||
        (taskTypeFilter === "recurring" && task.recurring) ||
        (taskTypeFilter === "non_recurring" && !task.recurring)
    );
};

const taskMatchesSearchQuery = (task, filters) => {
    const query = filters.value.search;
    return !query || task.description.toLowerCase().includes(query);
};

const updateTasks = (
    task,
    tasks,
    filters,
    isLastPage,
    isCurrentlyInLastPosition
) => {
    const { order_by: orderBy, sort_by: sortBy } = filters.value;
    const { position, isLastPosition } = determineTaskPosition({
        task,
        tasks,
        orderBy,
        sortBy,
    });

    if (!isLastPage.value && isLastPosition && !isCurrentlyInLastPosition) {
        return;
    }

    tasks.value = [
        ...tasks.value.slice(0, position),
        task,
        ...tasks.value.slice(position),
    ];
};

const removeTask = (tasks, task) => {
    return (tasks.value = tasks.value.filter((t) => t.id !== task.id));
};

const determineTaskPosition = ({ task, tasks, orderBy, sortBy }) => {
    const propertyMap = {
        category: (t) => t.category?.name || null,
        priority: (t) => t.priority?.name || null,
    };

    const isSortingByPriority = sortBy === "priority";
    const priorityOrder = ["high", "medium", "low"];

    const isSortingByDate = [
        "scheduled_for",
        "created_at",
        "completed_at",
    ].includes(sortBy);

    const getSortValue = (item, sortByKey) => {
        const resolveProperty = propertyMap[sortByKey];
        return resolveProperty ? resolveProperty(item) : item[sortByKey];
    };

    const value = getSortValue(task, sortBy);

    // null is a valid value.
    if (value === undefined) {
        throw new Error(
            `Task is missing corresponding property for "${sortBy}".`
        );
    }

    const orderFactor = orderBy === "asc" ? 1 : -1;

    const compare = (a, b) => {
        if (isSortingByPriority) {
            a = priorityOrder.indexOf(a.toLowerCase());
            b = priorityOrder.indexOf(b.toLowerCase());

            return (a - b) * orderFactor;
        }

        if (isSortingByDate) {
            return (new Date(a) - new Date(b)) * orderFactor;
        }

        // Fallback for null values.
        a = a || "";
        b = b || "";

        return a.localeCompare(b) * orderFactor;
    };

    const index = tasks.value.findIndex((t) => {
        const otherValue = getSortValue(t, sortBy);
        return compare(value, otherValue) < 0;
    });

    return {
        position: index === -1 ? tasks.value.length : index,
        isLastPosition: index === -1,
    };
};
